<script setup lang="ts">
import { computed, ref } from 'vue';
import { ComparisonResult, ScenarioInputs } from '../lib/types';
import { formatCurrency } from '../lib/math';

const props = defineProps<{
  scenario: ScenarioInputs;
  result: ComparisonResult;
}>();

const copied = ref(false);

const fmtPct = (value: number) => `${value.toFixed(2)}%`;

const report = computed(() => {
  const s = props.scenario;
  const r = props.result;

  const incomeCurrency = s.income.incomeCurrency;
  const fxRateLine = incomeCurrency === 'EUR' ? ` (FX ${s.income.fxRate})` : '';

  const lines = [
    `Scenario: ${s.meta.name}`,
    '',
    'Assumptions — general',
    `- Horizon: ${s.general.horizonYears} years` ,
    `- Annual return: ${fmtPct(s.general.annualReturnPct)} (${s.general.inflationMode || 'real'} mode; inflation ${fmtPct(s.general.inflationPct)})`,
    '',
    'Assumptions — income & life',
    `- Monthly income: ${s.income.monthlyIncome} ${incomeCurrency}${fxRateLine}`,
    `- Life-need monthly: ${formatCurrency(s.income.lifeNeedMonthly)}`,
    `- Annual leakage: ${fmtPct(s.income.leakagePct)}`,
    `- Buffer on investable: ${fmtPct(s.income.bufferPct)}`,
    '',
    'Assumptions — bookkeeping & admin (monthly)',
    `- JDG bookkeeping: ${formatCurrency(s.bookkeeping.jdgBookkeeping)}`,
    `- Sp. z o.o. bookkeeping: ${formatCurrency(s.bookkeeping.spBookkeeping)}`,
    `- Virtual office: ${formatCurrency(s.bookkeeping.virtualOffice)}`,
    `- Other admin: ${formatCurrency(s.bookkeeping.otherAdmin)}`,
    '',
    'Assumptions — JDG',
    `- Ryczałt: ${fmtPct(s.jdg.ryczaltPct)}`,
    `- ZUS mode: ${s.jdg.zusAsPercent ? `percent (${fmtPct(s.jdg.zusPercent)})` : `fixed ${formatCurrency(s.jdg.zusFixed)}`}`,
    `- Private cap gains tax: ${fmtPct(s.jdg.capGainsTaxPct)}`,
    `- Rebalancing: ${s.jdg.rebalance}`,
    `- Private leakage: ${fmtPct(s.jdg.privateLeakagePct)}`,
    '',
    'Assumptions — sp. z o.o.',
    `- CIT mode: ${s.spzoo.citMode}`,
    ...(s.spzoo.citMode === 'classic' ? [`- Classic CIT: ${fmtPct(s.spzoo.classicCITRatePct)}`] : []),
    ...(s.spzoo.citMode === 'estonian'
      ? [
          s.spzoo.estonian.useSimple
            ? `- Estonian mode: simple (effective ${fmtPct(s.spzoo.estonian.effectiveDistributionPct)})`
            : `- Estonian mode: detailed (CIT ${fmtPct(s.spzoo.estonian.detailed.estonianCITRatePct)}, PIT ${fmtPct(s.spzoo.estonian.detailed.dividendPITPct)}, offset ${fmtPct(s.spzoo.estonian.detailed.pitOffsetPct)})`
        ]
      : []),
    `- Company cap gains tax: ${fmtPct(s.spzoo.companyCapGainsTaxPct)}`,
    `- Company rebalancing: ${s.spzoo.rebalance}`,
    `- Tooling subscriptions: ${formatCurrency(s.spzoo.toolingSubsMonthly)}`,
    `- Dividend tax: ${fmtPct(s.spzoo.dividendTaxPct)}`,
    `- Keep funds inside company at end: ${s.spzoo.keepInCompany ? 'yes' : 'no'}`,
    '',
    'Assumptions — owner cashflow & solver',
    `- Solver enabled: ${s.owner.solverEnabled ? 'yes' : 'no'}`,
    `- Allow salary/dividend/loan: ${s.owner.allowSalary ? 'salary ' : ''}${s.owner.allowDividends ? 'dividends ' : ''}${s.owner.allowLoan ? 'loan' : ''}`.trim() || 'none',
    `- Salary share of life-need: ${fmtPct(s.owner.salaryPortionPct)}`,
    `- Dividend share of life-need: ${fmtPct(s.owner.dividendPortionPct)}`,
    `- Manual salary gross: ${formatCurrency(s.owner.manualSalaryGross)}`,
    `- Manual dividend gross: ${formatCurrency(s.owner.manualDividendGross)}`,
    `- PIT threshold: ${formatCurrency(s.owner.progressive.threshold)}`,
    `- PIT rates: lower ${fmtPct(s.owner.progressive.lowerRatePct)}, upper ${fmtPct(s.owner.progressive.upperRatePct)}, health ${fmtPct(s.owner.progressive.healthPct)}`,
    `- Dividend tax: ${fmtPct(s.owner.dividendTaxPct)}`,
    `- Loan: rate ${fmtPct(s.owner.loan.interestRatePct)}, tax on interest ${fmtPct(s.owner.loan.interestTaxPct)}, term ${s.owner.loan.termYears}y, ${s.owner.loan.interestOnly ? 'interest-only' : 'amortizing'}, limit ${formatCurrency(s.owner.loan.principalLimit)}, lump sum ${formatCurrency(s.owner.loan.lumpSumAmount, s.owner.loan.currency)}, year ${s.owner.loan.lumpSumYear}`,
    '',
    'Results — JDG',
    `- Wealth at horizon: ${formatCurrency(r.jdg.summary.wealthEnd)}`,
    `- Consumption delivered: ${formatCurrency(r.jdg.summary.consumptionDelivered)}`,
    `- Combined utility: ${formatCurrency(r.jdg.summary.combinedWealthAndConsumption)}`,
    `- Portfolio end (net): ${formatCurrency(r.jdg.summary.portfolioEndNet)}`,
    `- Avg monthly invested: ${formatCurrency(r.jdg.summary.monthlyInvestedAvg)}`,
    `- Taxes: income+payroll ${formatCurrency(r.jdg.summary.totalTaxes.income + r.jdg.summary.totalTaxes.payroll)}, cap gains ${formatCurrency(r.jdg.summary.totalTaxes.capGains)}`,
    '',
    'Results — sp. z o.o.',
    `- Wealth at horizon: ${formatCurrency(r.spzoo.summary.wealthEnd)}`,
    `- Consumption delivered: ${formatCurrency(r.spzoo.summary.consumptionDelivered)}`,
    `- Combined utility: ${formatCurrency(r.spzoo.summary.combinedWealthAndConsumption)}`,
    `- Portfolio end (net): ${formatCurrency(r.spzoo.summary.portfolioEndNet)}`,
    `- Avg monthly invested: ${formatCurrency(r.spzoo.summary.monthlyInvestedAvg)}`,
    `- Taxes: payroll+PIT ${formatCurrency(r.spzoo.summary.totalTaxes.income + r.spzoo.summary.totalTaxes.payroll)}, CIT ${formatCurrency(r.spzoo.summary.totalTaxes.cit)}, dividend/estonian ${formatCurrency(r.spzoo.summary.totalTaxes.dividend + r.spzoo.summary.totalTaxes.estonianDistribution)}, cap gains ${formatCurrency(r.spzoo.summary.totalTaxes.capGains)}`,
    '',
    'Comparison',
    `- Wealth delta (sp. z o.o. - JDG): ${formatCurrency(r.deltaNetWorth)}`,
    `- Combined delta: ${formatCurrency(r.deltaCombined ?? 0)}`,
    `- Breakeven year (portfolio): ${r.breakevenYear ?? 'Not reached'}`,
    '',
    'Warnings',
    ...(r.warnings.length ? r.warnings.map(w => `- ${w}`) : ['- None'])
  ];

  return lines.join('\n');
});

function copyReport() {
  navigator.clipboard.writeText(report.value);
  copied.value = true;
  setTimeout(() => (copied.value = false), 2000);
}
</script>

<template>
  <div class="card">
    <div class="section-title" style="margin-bottom: 0.75rem">
      <span>Report mode</span>
      <span class="badge">Printable snapshot</span>
    </div>
    <p class="muted">
      Full snapshot of current assumptions and outputs. Copy into notes or export for review.
    </p>
    <div class="toolbar" style="margin: 0.5rem 0 0.75rem">
      <button class="secondary" @click="copyReport">{{ copied ? 'Copied!' : 'Copy report' }}</button>
    </div>
    <textarea class="report" :value="report" readonly></textarea>
  </div>
</template>

<style scoped>
.report {
  width: 100%;
  min-height: 420px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem;
  font-family: 'JetBrains Mono', 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  background: #f8fafc;
  resize: vertical;
}
</style>
